import React, { Component, ReactElement } from 'react';

import { ContextMenuAPI } from '@philips-emr/tws-components-api';
import { Placement } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType, ContainerProvider } from '@philips-emr/tws-core';
import { arrayOf, bool, element, eventListener, oneOf, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { CloseEvent, OpenEvent } from '@philips-emr/tws-core/lib/component/types';
import { enumValues, isFunction, trigger } from '@philips-emr/tws-utils';

import { Popover } from '../..';
import { ReactComponent as ContextIcon } from '../../../resources/assets/components/menu-context.svg';

import './context-menu.scss';

interface ContextMenuProps {
  children: ReactElement | ReactElement[];
  onBeforeClose?: (event: CloseEvent) => void;
  onClose?: (event: CloseEvent) => void;
  onOpen?: (event: OpenEvent) => void;
  placement: Placement;
  preventDefault?: boolean;
  stopPropagation?: boolean;
  uuid: string;
}

interface ContextMenuState {
  open: boolean;
}

@AwesomeComponent({ type: ComponentType.NAVIGATION, api: ContextMenuAPI })
class ContextMenu extends Component<ContextMenuProps, ContextMenuState> {
  state: ContextMenuState = {
    open: false,
  };

  static propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    onBeforeClose: eventListener,
    onClose: eventListener,
    onOpen: eventListener,
    placement: oneOf(enumValues(Placement)),
    preventDefault: bool,
    stopPropagation: bool,
    uuid: string,
  };

  static defaultProps = {
    placement: Placement.BOTTOM,
  };

  onClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    const { preventDefault, stopPropagation } = this.props;

    if (preventDefault) {
      event.preventDefault();
    }

    if (stopPropagation) {
      event.stopPropagation();
    }

    this.popoverToggle();
  };

  onItemClick = (): void => {
    this.popoverToggle();
  };

  getTriggerEvent = (): Function => {
    const { onClose, onOpen } = this.props;
    const { open } = this.state;

    if (open) {
      return trigger(onClose);
    }

    return trigger(onOpen);
  };

  popoverToggle = (): void => {
    const { open } = this.state;

    if (open && !this.shouldClose()) {
      return;
    }

    const triggerEvent = this.getTriggerEvent();

    this.setState(({ open }) => ({ open: !open }), () => triggerEvent());
  };

  shouldClose = (): boolean => {
    const { onBeforeClose } = this.props;

    let close = true;
    const abort = (): void => {
      close = false;
    };

    if (isFunction(onBeforeClose)) {
      onBeforeClose(new CloseEvent({ abort }));
    }

    return close;
  };

  renderIcon(): ReactElement {
    return (
      <div className="c-context-menu__icon" onClick={this.onClick}>
        <ContextIcon />
      </div>
    );
  }

  renderChildren(): ReactElement {
    const { children, uuid } = this.props;

    const Container = ContainerProvider.provide(this);

    if (!Container) {
      return <div />;
    }

    return (
      <Popover.Content>
        <Container className="u-expand" onClick={this.onItemClick} uuid={uuid}>
          {children}
        </Container>
      </Popover.Content>
    );
  }

  render(): ReactElement {
    const { placement } = this.props;
    const { open } = this.state;

    return (
      <div className="c-context-menu">
        <Popover className="c-context-menu__popover" placement={placement} onClose={this.popoverToggle} open={open} padding={4}>
          {this.renderIcon()}
          {this.renderChildren()}
        </Popover>
      </div>
    );
  }
}

export default ContextMenu;
